<script setup>
import { useForm } from 'vee-validate'
import { storeToRefs } from 'pinia'
import Coins from 'assets/icons/coins.svg'
import CreditCard from 'assets/icons/creditcard.svg'
import ApplePay from 'assets/icons/applepay.svg'
import GooglePay from 'assets/icons/googlepay.svg'
import Check from 'assets/icons/check.svg'
import Info from 'assets/icons/info.svg'
import LoadingCircle from 'assets/illustrations/loading-circle.svg?url'
import { v4 as uuidv4 } from 'uuid'
import { StorageSerializers, useSessionStorage } from '@vueuse/core'
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder.vue'
import { useMessageStore } from '~/stores/messages'
import { useParkingStore } from '~/stores/parking'
import { PARKING_CONSTANTS } from '@/utils/parkingConstants'

// Define own pending state
const isAuthorizationPending = ref(false)

// Get runtime config
const config = useRuntimeConfig()

// Get center route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Get the transactionId either from route or state
const transactionId = computed(() => route?.params?.transactionId || useState('state_parkingPaymentTransactionId').value)
const correlationId = uuidv4()

// Iframe configs
const iframeElement = ref(null)
const iframeHeight = ref('0')
const creditCardData = ref(null)
const iframeIsLoading = ref(true)

// Get Iframe origin
const getIframeOrigin = computed(() => config.public.secuPayIframe)

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Get Webview global state
const isWebview = useState('state_isWebview')

// Load user data
const bearerToken = useState('state_bearerToken')
const oid = useState('state_userOid')

// Get user data
// const { data: user, suspense: userSuspense } = useFetchProfile({ bearerToken, oid })
const { data: user } = useFetchProfile({ bearerToken, oid })

/*
onServerPrefetch(async () => {
    if (bearerToken.value) {
        await userSuspense()
    }
})
*/

// Compute current user
const currentUser = computed(() => user?.value?.data)

// Fetch parking store
const parkingStore = useParkingStore()
const { updateTicketStatus } = parkingStore
const { ticketInfos, savedTicketVoucher, ticketStatus } = storeToRefs(parkingStore)

// Prepare form (prefill this values with stored data from parking store)
const { values, handleSubmit, isSubmitting } = useForm({
    paymentMethods: ''
})

// Fetch payment methods
/* const {
    data: paymentMethods,
    suspense: paymentMethodsSuspense,
    isPlaceholderData: isPaymentMethodsPlaceholderData
} = useFetchPaymentMethods({ currency: 'EUR' }) */

const {
    data: paymentMethods,
    isPlaceholderData: isPaymentMethodsPlaceholderData
} = useFetchPaymentMethods({ currency: 'EUR' })

// Fetch smart transaction
/*
const {
    data: smartTransaction,
    isError: smartTransactionError,
    suspense: smartTransactionSuspense,
    isPlaceholderData: smartTransactionPlaceholderData
} = useFetchTransaction({ transactionId, preventFatalError: true })
*/

const {
    data: smartTransaction,
    isError: smartTransactionError,
    isPlaceholderData: smartTransactionPlaceholderData
} = useFetchTransaction({ transactionId, preventFatalError: true })

// Prefetch data on the server side
/* onServerPrefetch(async () => {
    await paymentMethodsSuspense()
    await smartTransactionSuspense()
}) */

// Handle smart transaction error
watch(() => smartTransactionError.value, (newValue) => {
    if (newValue) {
        updateTicketStatus({
            success: false,
            message: staticKeys.value?.parking_step3_pay_error
        })
    }
}, { immediate: true })

// Check if all data is available
const showPaymentForm = computed(() => paymentMethods.value?.length > 0 && smartTransaction.value?.id)

// Compute pending state
const isPending = computed(() => smartTransactionPlaceholderData.value || isPaymentMethodsPlaceholderData.value)

// Config for pending state
const getTransactionPendingConfig = computed(() => {
    return {
        media: LoadingCircle,
        subline: staticKeys.value?.parking_step3_pending_headline,
        hasAnimation: true,
        isFullscreen: true,
        copy: staticKeys.value?.parking_step3_pending_copy,
    }
})

// Enable saved credit card feature
const enableSavedCreditCardFeature = computed(() => !smartTransaction.value?.disableSavedCreditCardFeature)

// Multiple saved credit cards
const hasMultipleSavedCreditCards = computed(() => smartTransaction.value?.availablePaymentMethods?.length > 1)

// Compute total price ticket
const getPriceTotal = computed(() => getFormattedPrice(smartTransaction.value?.basketInfo?.sum, 'EUR'))

// Define payment methods configs
const paymentMethodsConfig = computed(() => {
    return {
        creditcard: {
            id: 'creditcard',
            label: staticKeys.value?.parking_payment_method_creditcard,
            name: 'paymentMethods',
            val: 'creditcard',
            value: paymentMethods.value[0],
            iconClass: 'icon-credit-card',
            icon: CreditCard,
            axis: 'vertical',
            alignment: 'center'
        },
        applepay: {
            id: 'applepay',
            name: 'paymentMethods',
            val: paymentMethods.value[0],
            value: 'applepay',
            iconClass: 'icon-apple-pay',
            icon: ApplePay,
            axis: 'vertical',
            alignment: 'center'
        },
        googlepay: {
            id: 'googlepay',
            name: 'paymentMethods',
            val: paymentMethods.value[0],
            value: 'googlepay',
            iconClass: 'icon-google-pay',
            icon: GooglePay,
            axis: 'vertical',
            alignment: 'center'
        }
    }
})

// Get a payment methods list
const paymentMethodsList = computed(() => {
    return paymentMethods.value?.map((paymentMethod) => {
        return paymentMethodsConfig.value[paymentMethod]
    })
})

// Check if credit card is selected
const isCreditCard = computed(() => values.paymentMethods === 'creditcard')
const isApplePay = computed(() => values.paymentMethods === 'applepay')
const isGooglePay = computed(() => values.paymentMethods === 'googlepay')

// Hide iframe initially
const iframeVisible = ref(false)

const isIframeVisible = computed(() => iframeVisible.value)

// Check if credit card is saved
const hasSavedCreditCards = computed(() => smartTransaction.value?.availablePaymentMethods?.length > 0 && enableSavedCreditCardFeature.value)

// Check if mobile ticket payment
const isMobileTicketPaymentScope = computed(() => smartTransaction.value?.type === PARKING_CONSTANTS.MOBILE_TICKET_PAYMENT)
const isMultiPurposeCardChargeScope = computed(() => smartTransaction.value?.type === PARKING_CONSTANTS.MULTI_PURPOSE_CARD_CHARGE)

// Compute summary headline
const getSummaryHeadline = computed(() => {
    const mobilePaymentHeadline = staticKeys.value?.parking_step2_ticket_headline
    const topUpHeadline = staticKeys.value?.parking_prepaidcard_topup_summary_headline

    return isMobileTicketPaymentScope.value ? mobilePaymentHeadline : isMultiPurposeCardChargeScope.value ? topUpHeadline : ''
})

// Check if iframe is shown
const showCreditCardIframe = computed(() => {
    if (!isCreditCard.value) return false

    if (isIframeVisible.value) return true

    return !hasSavedCreditCards.value
})

// Check if saved credit cards are shown
const showSavedCreditCards = computed(() => {
    if (!isCreditCard.value) return false

    if (isIframeVisible.value) return false

    return hasSavedCreditCards.value
})

// Config edit cta
const editSavedCreditCardCtaConfig = computed(() => {
    return {
        buttonType: 'secondary',
        type: 'div',
        active: true,
        large: true,
        fn: () => handleCreditCardSwitch(true)
    }
})

// Get saved credit cards headline config
const savedCreditCardsHeadlineConfig = computed(() => {
    return {
        headline: {
            title: staticKeys.value?.parking_payment_saved_creditcard_headline
        },
        icon: {
            name: CreditCard
        },
        alignment: 'top',
    }
})

// Credit card selection
const selectedBoxIndex = ref(0)

// Skeleton placeholder classes for boxes
const getIframeBoxClass = computed(() => {
    return {
        'skeleton-placeholder-container': iframeIsLoading.value
    }
})

// Get checkbox config
const getCheckBoxConfig = computed(() => {
    return {
        value: true,
        id: 'reusable',
        label: staticKeys.value?.parking_payment_save_creditcard_label,
        name: 'reusable',
        isMultiLine: true
    }
})

// Config for submit button
const submitButtonConfig = computed(() => {
    return {
        visible: true,
        label: staticKeys.value?.parking_step3_pay_cta,
        buttonConfig: {
            type: 'submit',
            buttonType: 'primary',
            large: true,
            active: true
        }
    }
})

// Get form class
const getFormClass = computed(() => {
    return {
        'is-submitting': isSubmitting.value
    }
})

// Get credit card expiry date
const getCreditCardExpiryDate = (expMonth, expYear) => {
    if (!expMonth || !expYear) return ''

    // Ensure month has leaded zero if length is 1
    const formattedMonth = expMonth.toString().padStart(2, '0')

    return `${formattedMonth}/${expYear}`
}

// Watch for multiple saved credit cards and decide if iframe should be shown
/* watch(() => hasSavedCreditCards.value, (newValue) => { // Watcher callback
        if (!newValue) {
            iframeVisible.value = true
        }
    },
    {
        immediate: true
    }
) */

// Get credit card payload
const getIframeCreditCardPayload = computed(() => {
    return {
        container: {
            type: values.paymentMethods,
            reusable: values.reusable?.length > 0 ? values.reusable[0] : false,
            private: {
                owner: creditCardData.value?.owner?.value,
                pan: creditCardData.value?.cardNumber?.value,
                expYear: (creditCardData.value?.yearExp?.value) && parseInt(creditCardData.value?.yearExp?.value),
                expMonth: (creditCardData.value?.monthExp?.value) && parseInt(creditCardData.value?.monthExp?.value),
                issuer: creditCardData.value?.cardNumber?.company,
                transact_container: creditCardData.value?.encryptedData?.crypted_container,
                transact_skey_pubkey: creditCardData.value?.encryptedData?.crypted_skey,
                transact_skey_keyname: creditCardData.value?.encryptedData?.key_filename,
                transact_hash: creditCardData.value?.encryptedData?.container_hash
            }
        }
    }
})

// Get saved credit card payload
const getSavedCreditCardPayload = computed(() => {
    if (!hasSavedCreditCards.value) return
    return {
        container: {
            id: smartTransaction.value?.availablePaymentMethods[selectedBoxIndex.value].id
        }
    }
})

const getCreditCardPayload = computed(() => showCreditCardIframe.value ? getIframeCreditCardPayload.value : getSavedCreditCardPayload.value)

// Prefill iframe with saved user data if available
const getOwner = computed(() => {
    if (!currentUser.value || !currentUser.value?.firstName) return ''

    if (currentUser.value?.firstName && currentUser.value?.lastName) {
        return `${currentUser.value?.firstName} ${currentUser.value?.lastName}`
    }

    return currentUser.value?.firstName
})

/**
 * Show or hide the credit card iframe.
 *
 * @param {boolean} value
 */
const handleCreditCardSwitch = (value) => {
    iframeVisible.value = value
}

/**
 * Toggles the selection of a credit card box.
 *
 * Updates `selectedBoxIndex` to the given index if not already selected;
 * otherwise, sets it to `null` to deselect.
 *
 * @param {number} index - The index of the credit card box to toggle.
 * @param event
 */
const toggleSelection = (index, event) => {
    // Don't toggle if only 1 credit card is available
    if (!hasMultipleSavedCreditCards.value) return false

    // Don't toggle if user clicks on the edit cta
    if (event.target.closest('.btn-secondary')) return false

    selectedBoxIndex.value = selectedBoxIndex.value === index ? null : index
}

/**
 * Initializes the iframe and sends messages to set the owner, theme, and language.
 *
 * @return {void}
 */
const initIframe = (contentWindow) => {
    // Set owner (optional)
    contentWindow?.postMessage({
        action: 'set-owner',
        owner: getOwner.value
    },
    getIframeOrigin.value
    )

    // Set theme
    contentWindow?.postMessage({
        action: 'set-theme',
        theme: 1, // 0 = secupay, 1 = custom
    }, getIframeOrigin.value
    )

    // Set language (optional)
    contentWindow?.postMessage({
        action: 'set-lang',
        lang: 'de'
    }, getIframeOrigin.value
    )

    iframeIsLoading.value = false
}

// Handle post-message iframe events
if (import.meta.client) {
    /**
     * Respond on iframe messages
     */
    window.addEventListener('message', function (e) {
        // Check origin
        if (e.origin !== getIframeOrigin.value) {
            console.warn('bad origin ' + e.origin)
            return
        }

        switch (e.data?.action) {
            // Iframe height changed. This is raised on load, and after validation.
            case 'cc-iframe-height':
                iframeHeight.value = `${e.data.documentHeight}px`
                break

            // Iframe sends data. These may be valid or not.
            case 'prepared-cc-data':
                console.info('credit card data', e.data.data)
                creditCardData.value = e.data?.data
                break

            default:
                console.warn('unknown message ' + e.data.action, e.data)
                break
        }
    }, false)
}

// Define callback urls
const successURL = computed(() => `https://${config.public.host}/payment/redirect?redirectUrl=success&scope=${smartTransaction.value?.type}&isWebview=${!!isWebview.value}&transactionId=${transactionId.value}`)
const failureURL = computed(() => `https://${config.public.host}/payment/redirect?redirectUrl=failure&scope=${smartTransaction.value?.type}&isWebview=${!!isWebview.value}&transactionId=${transactionId.value}&correlationId=${correlationId}`)

/**
 *  Authorize payment error callback
 * @returns {Promise<void>}
 */
const authorizePaymentErrorCallback = async () => {
    await navigateTo(failureURL.value, { external: true })
}

/**
 *  Authorize payment success callback
 * @returns {Promise<void>}
 */

const authorizePaymentSuccessCallback = async (payload) => {
    const { data } = payload

    const allowedStatusCodes = [200, 201]

    const getRedirectUrl = allowedStatusCodes.includes(data?.statusCode)
        ? (data?.statusCode === 200 ? data?.data?.processingURL : successURL.value)
        : failureURL.value

    await navigateTo(getRedirectUrl, { external: true })
}

const { mutate } = useAuthorizePaymentMutation({
    transactionId: transactionId.value,
    correlationId,
    onSuccessCallback: authorizePaymentSuccessCallback,
    onErrorCallback: authorizePaymentErrorCallback
})

/**
 *  Authorizes the payment
 * @returns {Promise<void>}
 */
const authorizePayment = async () => {
    // Initialize session storage with initial data
    const cachedParkingData = useSessionStorage(
        'parkingPaymentSession',
        null,
        {
            serializer: StorageSerializers.object
        })

    // Set session storage with some parking data
    cachedParkingData.value = {
        searchTermType: ticketInfos.value?.searchTermType,
        searchTerm: ticketInfos.value?.searchTerm,
        centerUid: centerRoute.value,
        voucher: savedTicketVoucher.value
    }

    // Wait for the next tick (to ensure that the session storage has been set)
    await nextTick()

    // We show layer until page reloads
    isAuthorizationPending.value = true

    mutate({
        body: {
            ...getCreditCardPayload.value,
            callbackURLs: {
                successURL: successURL.value,
                failureURL: failureURL.value
            },
        },
        dynamicUrlPart: values?.paymentMethods,
    })
}

// Watch for credit card data and react to internal iframe validation
watch(creditCardData, async () => {
    // Now check if the form is valid AND payment method was selected
    if (creditCardData.value?.allFieldsAreValid && showCreditCardIframe.value) {
        await authorizePayment()
    } else {
        if (import.meta.client && iframeElement.value) {
            // Scroll to iframe
            await nextTick()

            window.scrollBy({
                top: iframeElement.value.getBoundingClientRect().top - 100,
                behavior: 'smooth'
            })
        }
    }
})

// Reset credit card iframe when payment method is changed
watch(() => values, () => {
    if (!isCreditCard.value) {
        iframeVisible.value = false
    }
}, { deep: true, immediate: false })

// Submit form
const onSubmit = handleSubmit(async () => {
    // Track user click
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.PARKING_TICKET_PAYMENT_STEP3_CLICK,
    })

    // If user selected iframe credit card data, handle payment
    if (showCreditCardIframe.value) {
        // Make CC iframe validate the form and send back the data
        iframeElement.value?.contentWindow?.postMessage({ action: 'get-cc-data' }, getIframeOrigin.value)
    }

    // If a user selected saved credit card data, handle payment
    if (showSavedCreditCards.value) {
        await authorizePayment()
    }
})

/**
 * Resets the status object with the given value.
 *
 * @param {any} value - The new value to set the status object to.
 * @return {void} This function does not return anything.
 */
const resetStatusObj = (value) => {
    updateTicketStatus(value)
}

// Track Page Load
trackPageLoad({
    title: TRACKING_CONSTANTS.TITLES.PARKING_TICKET_PAYMENT_STEP3,
})

// Markdown
const { $md } = useNuxtApp()
const legalText = computed(() => $md.render(staticKeys.value?.parking_payment_saved_creditcard_legal || ''))
const legalNote = computed(() => $md.renderInline(staticKeys.value?.parking_payment_saved_creditcard_legal_note || ''))
</script>

<template>
    <Transition v-if="isAuthorizationPending" name="fade">
        <ParkingPaymentTransactionStatus :parking-payment-transaction-status-config="getTransactionPendingConfig" />
    </transition>

    <template v-else>
        <FormMessage :form-message-config="ticketStatus" @update:set-status="resetStatusObj" />

        <div v-if="showPaymentForm || isPending" class="parking-payment-summary-container">
            <SideBarHeadline :sidebar-headline-config="{ title: getSummaryHeadline }" />

            <DefaultContentBox v-if="isPending" class="skeleton-placeholder-container">
                <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'text' }" />
            </DefaultContentBox>

            <DefaultContentBox v-else :content-box-config="{ type: 'small' }">
                <table class="parking-payment-summary-table">
                    <tbody>
                        <tr>
                            <td><Coins /></td>
                            <td>{{ staticKeys?.parking_step2_cost_total }}</td>
                            <td><span class="parking-payment-summary-total">{{ getPriceTotal }}</span></td>
                        </tr>
                    </tbody>
                </table>
            </DefaultContentBox>

            <form class="parking-payment-methods-form" :class="getFormClass" @submit.prevent="onSubmit">
                <SideBarHeadline :sidebar-headline-config="{ title: staticKeys?.parking_payment_methods }" />

                <DefaultContentBox v-if="isPending" class="skeleton-placeholder-container">
                    <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'text' }" />
                </DefaultContentBox>

                <FormRadioGroup v-else>
                    <FormRadio
                        v-for="paymentMethod in paymentMethodsList"
                        :key="paymentMethod?.id"
                        :radio-button-config="paymentMethod"
                    />
                </FormRadioGroup>

                <template v-if="isPending">
                    <br>
                    <DefaultContentBoxList>
                        <DefaultContentBox class="skeleton-placeholder-container">
                            <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'multi-headline' }" />
                            <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'text' }" />
                            <br>
                            <br>
                            <br>
                            <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'button' }" />
                        </DefaultContentBox>
                    </DefaultContentBoxList>
                </template>

                <template v-else>
                    <DefaultContentBoxList v-if="showCreditCardIframe">
                        <DefaultContentBox
                            :content-box-config="{ type: 'small' }"
                            class="parking-payment-methods-credit-card-wrapper"
                            :class="getIframeBoxClass"
                        >
                            <div class="parking-payment-methods-iframe-container">
                                <div v-if="iframeIsLoading" class="parking-payment-iframe-loading-content">
                                    <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'multi-headline' }" />
                                    <component :is="SkeletonPlaceholder" :skeleton-placeholder-config="{ type: 'text', iterator: 4 }" />
                                </div>

                                <iframe
                                    ref="iframeElement"
                                    :src=" getIframeOrigin"
                                    width="100%"
                                    height="400px"
                                    @load="initIframe($event.target?.contentWindow)"
                                />

                                <template v-if="enableSavedCreditCardFeature && !iframeIsLoading">
                                    <FormCheckBoxGroup>
                                        <FormCheckBox :checkbox-config="getCheckBoxConfig" />
                                    </FormCheckBoxGroup>

                                    <p class="helper-text" v-html="legalText" />
                                </template>
                            </div>
                        </DefaultContentBox>
                    </DefaultContentBoxList>

                    <DefaultContentBoxList v-if="showSavedCreditCards">
                        <DefaultContentBox
                            v-for="(creditCard, index) in smartTransaction?.availablePaymentMethods"
                            :key="`credit-card-${index}`"
                            :content-box-config="{ type: 'small' }"
                            class="parking-payment-methods-credit-card-wrapper"
                            @click="toggleSelection(index, $event)"
                        >
                            <Check
                                v-if="selectedBoxIndex === index && hasMultipleSavedCreditCards"
                                class="parking-payment-selected-credit-card"
                                :title="staticKeys?.parking_payment_saved_creditcard_selected"
                            />

                            <header class="parking-info-box-header">
                                <DefaultContentBoxHeadline :content-box-headline-config="savedCreditCardsHeadlineConfig" />
                            </header>

                            <table class="parking-payment-saved-credit-card-table">
                                <tbody>
                                    <tr v-if="creditCard?.owner">
                                        <td colspan="2">
                                            <strong>{{ staticKeys?.parking_payment_saved_creditcard_owner }}</strong>

                                            {{ creditCard.owner }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <strong>{{ staticKeys?.parking_payment_saved_creditcard_number }}</strong>
                                            {{ creditCard?.pan }}
                                        </td>
                                        <td>
                                            <strong>{{ staticKeys?.parking_payment_saved_creditcard_expiry_date }}</strong>
                                            {{ getCreditCardExpiryDate(creditCard?.expMonth, creditCard?.expYear) }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <BaseButton :base-button-config="editSavedCreditCardCtaConfig">
                                {{ staticKeys?.parking_payment_saved_creditcard_edit_cta }}
                            </BaseButton>
                        </DefaultContentBox>
                    </DefaultContentBoxList>

                    <DefaultContentBoxList class="legal-helper-note-container">
                        <p class="helper-text legal-helper-note">
                            <Info />
                            <span v-html="legalNote" />
                        </p>
                    </DefaultContentBoxList>

                    <div class="parking-payment-methods-cta">
                        <FormButton v-if="isCreditCard" :form-button-config="submitButtonConfig" />
                        <ParkingPaymentApplePay v-if="isApplePay" />
                        <ParkingPaymentGooglePay v-if="isGooglePay" />
                    </div>
                </template>
            </form>

            <DevOnly><pre>getCreditCardPayload {{ getCreditCardPayload }}</pre></DevOnly>
            <DevOnly><pre>{{ smartTransaction }}</pre></DevOnly>
        </div>
    </template>
</template>

<style lang="scss">
.parking-payment-summary-container {
    margin-block: px2rem(24) px2rem(60);

    @include bp(medium1) {
        margin-block: px2rem(40) px2rem(100);
    }
}

.parking-payment-saved-credit-card-table {
    width: 100%;
    margin-block-end: px2rem(30);

    @include body2-medium;

    tbody {
        td {
            padding-block-end: px2rem(5);

            strong {
                @include body2-extrabold;

                display: block;
            }
        }
    }
}

.parking-payment-summary-table {
    width: 100%;

    @include body2-medium;

    tbody {
        @include body2-extrabold;

        color: $parking-payment-amount-table;

        tr {
            td {
                vertical-align: middle;

                &:first-child {
                    width: 1px;
                }

                &:last-child {
                    text-align: right;
                }

                svg {
                    fill: $parking-payment-amount-table;;
                    width: 24px;
                    height: 24px;
                    margin-inline-end: px2rem(15)
                }
            }
        }
    }
}

.parking-payment-summary-total {
    display: block;
    white-space: nowrap;
}

.parking-payment-methods-form {
    margin-block-start: px2rem(60);
}

.parking-payment-methods-credit-card-wrapper {
    margin-block-start: px2rem(10);
    position: relative;
    cursor: pointer;

    &:only-child {
        cursor: default;
    }

    &:has(.parking-payment-selected-credit-card) {
        animation: glow2 2s ease-in-out forwards;
        cursor: default;
    }
}

.parking-payment-selected-credit-card {
    fill: $parking-payment-selected-credit-card;
    position: absolute;
    top: 24px;
    right: 16px;
    width: 24px;
}

.parking-payment-methods-iframe-container {
    width: 100%;

    iframe {
        border: 0 none;
        height: v-bind('iframeHeight');
        mix-blend-mode: multiply;
        margin-block-end: px2rem(10);
        max-width: 750px;
    }
}

.parking-payment-iframe-loading-content {
    margin-block-end: px2rem(40)
}

.parking-payment-methods-cta {
    margin-block-start: px2rem(30);
    display: flex;
    justify-content: center;
    width: 100%;

    .form-submit-wrapper {
        width: 100%;
    }
}

.legal-helper-note-container {
    margin-block: px2rem(-10) px2rem(30);

    @include bp(large1) {
        margin-block: px2rem(-30) px2rem(30);
    }
}

.legal-helper-note {
    display: grid;
    grid-template-columns: auto 1fr;

    svg {
        fill: $helper-text;
        width: 20px;
        height: 20px;
        margin-inline-end: px2rem(10);
    }
}
</style>
